import { supabase } from "../supabase"

export interface AdminBooking {
  id: string
  user_id: string
  created_at: string
  slot_ids: string[]
  team_members: Array<{ name: string; phone_number?: string }>
}

export interface AdminBookingWithDetails extends AdminBooking {
  slots: Array<{
    id: string
    date: string
    time_start: string
    time_end: string
    status: string
  }>
}

export class AdminBookingService {
  // Create admin booking in admin_bookings table
  static async createAdminBooking(bookingData: {
    user_id: string;
    slot_ids: string[];
    team_members: Array<{ name: string; phone_number?: string }>;
  }): Promise<string | null> {
    try {
      // First, cancel any existing user bookings for these slots
      const { data: existingBookings } = await supabase
        .from('bookings')
        .select('id, slot_id')
        .in('slot_id', bookingData.slot_ids)
        .neq('status', 'rejected');
      
      if (existingBookings && existingBookings.length > 0) {
        const bookingIds = existingBookings.map(b => b.id);
        // Mark existing bookings as rejected and set rejection_reason
        await supabase
          .from('bookings')
          .update({ 
            status: 'rejected',
            rejection_reason: 'Slot blocked for admin booking'
          })
          .in('id', bookingIds);
      }

      // Then block the slots
      const { error: slotError } = await supabase
        .from("slot")
        .update({
          booked_by_user_id: bookingData.user_id,
          status: "blocked",
          block_reason: "Admin Booking",
        })
        .in("id", bookingData.slot_ids)

      if (slotError) {
        console.error("Error blocking slots for admin booking:", slotError)
        return null
      }

      // Then create the admin booking record
      const { data, error } = await supabase
        .from("admin_bookings")
        .insert({
          user_id: bookingData.user_id,
          slot_ids: bookingData.slot_ids,
          team_members: bookingData.team_members,
        })
        .select()
        .single()

      if (error) {
        console.error("Error creating admin booking:", error)
        // Rollback slot blocking if booking creation fails
        await supabase
          .from("slot")
          .update({
            booked_by_user_id: null,
            status: "pending",
            block_reason: null,
          })
          .in("id", bookingData.slot_ids)
        return null
      }

      return data.id
    } catch (error) {
      console.error("Error in createAdminBooking:", error)
      return null
    }
  }

  // Get all admin bookings with slot details
  static async getAllAdminBookings(): Promise<AdminBookingWithDetails[]> {
    try {
      const { data: adminBookings, error } = await supabase
        .from("admin_bookings")
        .select("*")
        .order("created_at", { ascending: false })

      if (error) {
        console.error("Error getting admin bookings:", error)
        return []
      }

      if (!adminBookings || adminBookings.length === 0) {
        return []
      }

      // Get slot details for each admin booking
      const bookingsWithDetails: AdminBookingWithDetails[] = []

      for (const booking of adminBookings) {
        const { data: slots, error: slotsError } = await supabase
          .from("slot")
          .select("id, date, time_start, time_end, status")
          .in("id", booking.slot_ids)

        if (slotsError) {
          console.error("Error getting slots for admin booking:", slotsError)
          continue
        }

        bookingsWithDetails.push({
          ...booking,
          slots: slots || []
        })
      }

      return bookingsWithDetails
    } catch (error) {
      console.error("Error in getAllAdminBookings:", error)
      return []
    }
  }

  // Cancel admin booking and unblock slots
  static async cancelAdminBooking(bookingId: string): Promise<boolean> {
    try {
      // Get the admin booking details
      const { data: adminBooking, error: fetchError } = await supabase
        .from("admin_bookings")
        .select("*")
        .eq("id", bookingId)
        .single()

      if (fetchError || !adminBooking) {
        console.error("Admin booking not found:", fetchError)
        return false
      }

      // Unblock the slots
      const { error: slotError } = await supabase
        .from("slot")
        .update({
          booked_by_user_id: null,
          status: "pending",
          block_reason: null,
        })
        .in("id", adminBooking.slot_ids)

      if (slotError) {
        console.error("Error unblocking slots:", slotError)
        return false
      }

      // Delete the admin booking record
      const { error: deleteError } = await supabase
        .from("admin_bookings")
        .delete()
        .eq("id", bookingId)

      if (deleteError) {
        console.error("Error deleting admin booking:", deleteError)
        return false
      }

      return true
    } catch (error) {
      console.error("Error in cancelAdminBooking:", error)
      return false
    }
  }

  // Get admin bookings for a specific date
  static async getAdminBookingsForDate(date: string): Promise<AdminBookingWithDetails[]> {
    try {
      const allBookings = await this.getAllAdminBookings()
      
      // Filter bookings that have slots for the specified date
      return allBookings.filter(booking => 
        booking.slots.some(slot => slot.date === date)
      )
    } catch (error) {
      console.error("Error in getAdminBookingsForDate:", error)
      return []
    }
  }
}